import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import FrictionlessLogin, { frictionlessLoginState } from './FrictionlessLogin';
import { FrictionlessLoginElevateOtp } from './FrictionlessLoginElevateOtp';
import { makeDebounce } from './debounce';
import { useReportFrictionlessEvent } from './useFrictionlessLoginLogs';

import { getStoredPaymentMethodInfo } from 'businessLogic/Insight/Reporting';
import { ERROR_FRICTIONLESS_LOGIN_OTP_REMEDIATION } from 'businessLogic/Wallet';
import SegmentIO from 'reporting/SegmentIO';
import {
  frictionlessLoginEmailSelector,
  frictionlessLoginSessionIdSelector,
  isFrictionlessLoginEnabledSelector,
} from 'store/auth/selectors';
import { authActions, checkFrictionlessLogin, refreshAuth, signIn } from 'store/auth/slice';
import { saleSelectors } from 'store/sale/selectors';
import { getStore } from 'store/store';
import { useSelector } from 'store/utils';
import { walletSelectors } from 'store/wallet/selectors';
import { fetchWallets } from 'store/wallet/slice';

interface FrictionlessLoginContainerProps {
  showCloseButton?: boolean;
}

const debounce = makeDebounce();

const frictionlessLoginTracker = (() => {
  /*
    Since this component is momentarily unmounted while we're fetching wallets,
    setState will be default once the component is remounted, hence we need a global
    tracker to show if it has been set once
   */
  let isJustLoggedInWithFrictionless = false;
  return {
    setIsJustLoggedInWithFrictionless: () => {
      isJustLoggedInWithFrictionless = true;
    },
    getIsJustLoggedInWithFrictionless: () => isJustLoggedInWithFrictionless,
  };
})();

const FrictionlessLoginContainer: React.FC<FrictionlessLoginContainerProps> = ({
  showCloseButton,
}) => {
  const [stepState, setStepState] = useState<frictionlessLoginState>('step-1');
  const dispatch = useDispatch();
  const isEnabled = useSelector(isFrictionlessLoginEnabledSelector);
  const email = useSelector(frictionlessLoginEmailSelector);
  const sessionId = useSelector(frictionlessLoginSessionIdSelector);
  const saleType = useSelector((store) => saleSelectors.typeSelector(store.sale));
  const fetchWalletErrorMessage = useSelector((s) =>
    walletSelectors.fetchWalletErrorMessage(s.wallet)
  );

  const frictionlessLogger = useReportFrictionlessEvent();

  const shouldHide = !isEnabled || !email || !sessionId;

  const isSubscription = saleType === 'SUBSCRIPTION';

  const logTokenFrictionlessEnabled = () => {
    // when the component init sessionId & email will always be empty, so we want to only log once the process is likely to be finished
    debounce(
      () =>
        frictionlessLogger.log({
          event: 'report',
          message: 'is-frictionless-enabled',
          extraParams: {
            isEnabled: Boolean(email && sessionId),
          },
        }),
      1000
    );
  };

  useEffect(() => {
    logTokenFrictionlessEnabled();
  }, [email, sessionId]);

  useEffect(() => {
    // @ts-expect-error
    dispatch(checkFrictionlessLogin());
    return () => {
      // Clear identity error when component unmounts
      dispatch(authActions.setFrictionlessIdentityError({ showError: false }));
    };
  }, []);

  useEffect(() => {
    if (!shouldHide) {
      // Reset stepState to step-1 when modal should show again
      if (showCloseButton) setStepState('step-1');
      frictionlessLogger.trackLoginOpened();
      frictionlessLogger.log({
        message: 'frictionless-modal-displayed',
      });
    }
  }, [shouldHide]);

  // Note: frictionlessLoginSuccess is now called in handleIdentityWidgetComplete after fetchWallets
  // to ensure stored payment method info is available for tracking

  const handleContinueButtonClick = () => {
    SegmentIO.frictionlessLoginSubmit();
    setStepState('step-2');
  };

  const handleNotMeButtonClick = () => {
    SegmentIO.frictionlessLoginNotYou();
    frictionlessLogger.log({
      message: 'not-me-button-clicked',
      extraParams: {
        userClosedModal: true,
      },
    });
    // @ts-expect-error
    dispatch(signIn());
  };

  const handleHide = (source: 'before_sure' | 'after_sure') => {
    // Clear any identity error when closing
    dispatch(authActions.setFrictionlessIdentityError({ showError: false }));

    // If showCloseButton is true, handle it as a close button click
    if (showCloseButton) {
      // Reset email and session ID
      dispatch(authActions.clearFrictionlessLoginState());
      frictionlessLogger.log({
        message: 'user-closed-modal-with-x-button',
        extraParams: {
          userClosedModal: true,
          source: 'x-button',
        },
      });
    } else {
      // Original behavior for other close actions
      SegmentIO.frictionlessLoginCancel(source);
      frictionlessLogger.log({
        message: 'user-closed-modal',
        extraParams: {
          userClosedModal: true,
        },
      });
    }
    setStepState('cancelled');
  };

  const handleOtpCancel = () => {
    // Clear any identity error when cancelling
    dispatch(authActions.setFrictionlessIdentityError({ showError: false }));

    // Only clear state and close modal for subscription flows
    if (isSubscription) {
      // Clear the frictionless login state to prevent the modal from showing again
      dispatch(authActions.clearFrictionlessLoginState());
    }

    SegmentIO.frictionlessLoginOtpX();
    frictionlessLogger.log({
      message: 'user-closed-otp',
      extraParams: {
        userClosedModal: true,
      },
    });
    setStepState('cancelled');
  };

  const handleIdentityWidgetComplete = async () => {
    // Clear any identity error when completing successfully
    dispatch(authActions.setFrictionlessIdentityError({ showError: false }));

    frictionlessLogger.log({
      message: 'frictionless-login-complete',
      event: 'done',
    });
    // @ts-ignore
    await dispatch(refreshAuth());
    frictionlessLoginTracker.setIsJustLoggedInWithFrictionless();
    // @ts-ignore
    await dispatch(checkFrictionlessLogin());
    // @ts-ignore
    await dispatch(fetchWallets());

    // Fire frictionlessLoginSuccess after fetchWallets completes so wallet info is available
    const store = getStore();
    const state = store.getState();
    const storedPaymentMethodInfo = getStoredPaymentMethodInfo(
      state.wallet?.userWallets,
      state.featureFlags
    );
    SegmentIO.frictionlessLoginSuccess(state.auth?.authTicket, storedPaymentMethodInfo);

    // Set frictionless checkout as completed for subscription phase 2 logging only
    if (showCloseButton) {
      dispatch(authActions.setFrictionlessCheckoutComplete({ isCompleted: true }));
    }
  };

  const handleIdentityWidgetError = (error: Error) => {
    dispatch(
      authActions.setFrictionlessIdentityError({
        showError: true,
        errorMessage: error?.message || 'Verification error',
      })
    );

    SegmentIO.frictionlessLoginError();
    frictionlessLogger.trackLoginError('identity');
    frictionlessLogger.log({
      logLevel: 'error',
      error,
      message: 'frictionless-login-identity-widget-error',
    });
    if (error?.message === 'INELIGIBLE_EMAIL_ERROR') {
      setStepState('cancelled');
    }
  };

  const handleIdentityWidgetReady = (startedAt: Date) => {
    const end = Date.now();
    const executionTimeMs = Date.now() - startedAt.getTime();
    frictionlessLogger.log({
      message: 'frictionless-login-identity-widget-ready',
      event: 'ready',
      rum: {
        executionTimeMs,
        end,
        start: startedAt.getTime(),
      },
    });
  };

  if (fetchWalletErrorMessage === ERROR_FRICTIONLESS_LOGIN_OTP_REMEDIATION) {
    return (
      <FrictionlessLoginElevateOtp
        onComplete={handleIdentityWidgetComplete}
        onError={handleIdentityWidgetError}
        onOtpCancel={handleOtpCancel}
      />
    );
  }

  if (shouldHide) {
    return null;
  }

  return (
    <FrictionlessLogin
      email={email}
      sessionId={sessionId}
      stepState={stepState}
      onContinueButtonClick={handleContinueButtonClick}
      onComplete={handleIdentityWidgetComplete}
      onError={handleIdentityWidgetError}
      onReady={handleIdentityWidgetReady}
      onOtpCancel={handleOtpCancel}
      onHide={handleHide}
      onNotMeButtonClick={handleNotMeButtonClick}
      isGuestCheckoutAllowed={!isSubscription}
      showCloseButton={showCloseButton}
    />
  );
};

export default FrictionlessLoginContainer;
